import { createServerFn } from "@tanstack/react-start";
import { generateText, Output } from "ai";
import { z } from "zod";
import { createLovableAiGatewayProvider } from "./ai-gateway.server";

const ProjectInput = z.object({
  street: z.string().optional().default(""),
  houseNumber: z.string().optional().default(""),
  zip: z.string().optional().default(""),
  city: z.string().optional().default(""),
  houseType: z.string().optional().default(""),
  units: z.string().optional().default(""),
  yearBuilt: z.string().optional().default(""),
  construction: z.string().optional().default(""),
  windowMaterial: z.string().optional().default(""),
  windowGlazing: z.string().optional().default(""),
  entranceDoorMaterial: z.string().optional().default(""),
  entranceDoorCondition: z.string().optional().default(""),
  interiorDoorsMaterial: z.string().optional().default(""),
  interiorDoorsCondition: z.string().optional().default(""),
  roofType: z.string().optional().default(""),
  roofInsulated: z.string().optional().default(""),
  renovationsDone: z.string().optional().default(""),
  renovationsDetails: z.string().optional().default(""),
  heatingType: z.string().optional().default(""),
  heatingConsumption: z.string().optional().default(""),
  electricityConsumption: z.string().optional().default(""),
  electricityProvider: z.string().optional().default(""),
  energyCertificate: z.string().optional().default(""),
  energyClass: z.string().optional().default(""),
  taxClass: z.string().optional().default(""),
  taxRate: z.string().optional().default(""),
  taxableIncome: z.string().optional().default(""),
  projectGoal: z.string().optional().default(""),
  projectDescription: z.string().optional().default(""),
  freeText: z.string().optional().default(""),
});

// ---------------------------------------------------------------------------
// Schema (Phase Fix): robust, mit Pflicht-Kernfeldern + optionalen Detailblöcken
// ---------------------------------------------------------------------------
// Pflichtfelder (immer erforderlich):
//   projectSummary, detectedMeasures, risks, recommendations, trustLevel, missingData
// Alle weiteren Felder (Standort/PV/WP/Sanierung/Förderung/Steuer/Kosten) sind
// optional und werden bei Bedarf mit Defaults aufgefüllt (Response-Repair).
// ---------------------------------------------------------------------------

const num = (def = 0) => z.coerce.number().optional().default(def);
const str = (def = "") => z.string().optional().default(def);

const StandortSchema = z.object({
  adresse: str(),
  region: str(),
  sonnenstundenJahr: num(),
  globalstrahlungKwhM2: num(),
  bemerkungStandort: str(),
}).default({});

const PhotovoltaikSchema = z.object({
  empfohleneKwp: num(),
  anzahlModule: num(),
  moduleLeistungWp: num(440),
  erwarteteJahreserzeugungKwh: num(),
  speicherEmpfohlenKwh: num(),
  investitionMaxEur: num(),
  begruendung: str(),
}).default({});

const WaermepumpeSchema = z.object({
  heizlastKw: num(),
  anzahl: num(),
  leistungProGeraetKw: num(),
  typ: str(),
  investitionMaxEur: num(),
  begruendung: str(),
}).default({});

const SanierungItem = z.object({
  massnahme: str(),
  investitionMaxEur: num(),
  energieeinsparungProzent: num(),
});

const FoerderungItem = z.object({
  programm: str(),
  traeger: str(),
  bezug: str(),
  maxFoerderungEur: num(),
  tilgungszuschussProzent: num(),
  zinssatzProzent: num(),
  hinweis: str(),
});

const SteuerSchema = z.object({
  afaProzentJahr: num(),
  afaBetragJahrEur: num(),
  eabSinnvoll: z.coerce.boolean().optional().default(false),
  eabBegruendung: str(),
  steuerersparnisJahrEur: num(),
}).default({});

const IstSchema = z.object({
  stromkostenJahrEur: num(),
  heizkostenJahrEur: num(),
  gesamtJahrEur: num(),
}).default({});

const ZukunftSchema = z.object({
  stromkostenJahrEur: num(),
  heizkostenJahrEur: num(),
  zusatzeinnahmenJahrEur: num(),
  zusatzeinnahmenBegruendung: str(),
  gesamtSaldoJahrEur: num(),
}).default({});

const InvestSchema = z.object({
  bruttoEur: num(),
  foerderungAbzugEur: num(),
  nettoEur: num(),
  finanzierungVorschlag: str(),
  amortisationJahre: num(),
}).default({});

// Toleranter Coercer: viele Modelle liefern Arrays von Strings statt Objekten.
// Wir akzeptieren beides und normalisieren auf das Zielschema.
const PrioEnum = z.enum(["hoch", "mittel", "niedrig"]).catch("mittel");

const DetectedMeasureSchema = z.preprocess(
  (v) => typeof v === "string" ? { massnahme: v } : v,
  z.object({
    massnahme: str(),
    begruendung: str(),
    prioritaet: PrioEnum.optional().default("mittel"),
  }),
);

const RiskSchema = z.preprocess(
  (v) => typeof v === "string" ? { risiko: v } : v,
  z.object({
    risiko: str(),
    schwere: PrioEnum.optional().default("mittel"),
  }),
);

const RecommendationSchema = z.preprocess(
  (v) => typeof v === "string" ? { empfehlung: v } : v,
  z.object({
    empfehlung: str(),
    prioritaet: PrioEnum.optional().default("mittel"),
  }),
);

const AnalysisSchema = z.object({
  // Pflichtfelder (Kern)
  projectSummary: z.string().min(1).default("Analyse erstellt mit eingeschränkter Datenbasis."),
  detectedMeasures: z.array(DetectedMeasureSchema).default([]),
  risks: z.array(RiskSchema).default([]),
  recommendations: z.array(RecommendationSchema).default([]),
  trustLevel: z.enum(["A", "B", "C", "D", "E"]).default("D"),
  missingData: z.array(z.string()).default([]),

  // Optionale Detailblöcke (für AnalysisReport)
  standort: StandortSchema,
  photovoltaik: PhotovoltaikSchema,
  waermepumpe: WaermepumpeSchema,
  sanierung: z.array(SanierungItem).default([]),
  foerderung: z.array(FoerderungItem).default([]),
  steuer: SteuerSchema,
  istSituation: IstSchema,
  zukunftSituation: ZukunftSchema,
  investitionGesamt: InvestSchema,
  empfehlung: str("Bitte Eingaben prüfen und Analyse ggf. erneut starten."),
  hinweise: z.array(z.string()).default([]),
});

export type AnalysisResult = z.infer<typeof AnalysisSchema>;

// ---------------------------------------------------------------------------
// Response-Repair: extrahiert JSON aus rohem Modell-Text
// ---------------------------------------------------------------------------
function extractJsonFromResponse(text: string): unknown | null {
  if (!text) return null;
  const cleaned = text
    .replace(/```json\s*/gi, "")
    .replace(/```\s*/g, "")
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, "")
    .trim();
  try { return JSON.parse(cleaned); } catch { /* try harder */ }
  const m = cleaned.match(/\{[\s\S]*\}/);
  if (m) {
    let candidate = m[0];
    // Trailing-Comma fix
    candidate = candidate.replace(/,(\s*[}\]])/g, "$1");
    try { return JSON.parse(candidate); } catch { /* give up */ }
  }
  return null;
}

function buildFallback(partial: Partial<AnalysisResult> | null, reason: string): AnalysisResult {
  const base = AnalysisSchema.parse({
    projectSummary: "Die Analyse konnte nicht vollständig strukturiert werden.",
    detectedMeasures: [],
    risks: [{ risiko: reason, schwere: "mittel" }],
    recommendations: [
      { empfehlung: "Bitte Eingaben vervollständigen und Analyse erneut starten.", prioritaet: "hoch" },
    ],
    trustLevel: "E",
    missingData: ["strukturierte_ki_antwort"],
  });
  if (!partial) return base;
  // Versuch: bekannte Felder mergen
  const merged = { ...base, ...partial } as AnalysisResult;
  const safe = AnalysisSchema.safeParse(merged);
  return safe.success ? safe.data : base;
}

export const runProjectAnalysis = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => ProjectInput.parse(input))
  .handler(async ({ data }) => {
    const key = process.env.LOVABLE_API_KEY;
    if (!key) {
      console.error("[runProjectAnalysis] LOVABLE_API_KEY fehlt");
      return {
        ok: false as const,
        error: "Die Analyse konnte nicht vollständig strukturiert werden. Bitte prüfen Sie die Eingaben oder starten Sie die Analyse erneut.",
      };
    }

    const gateway = createLovableAiGatewayProvider(key);
    const model = gateway("google/gemini-3-flash-preview");

    const prompt = `Du bist ein deutscher Energie- und Fördermittel-Berater. Erstelle eine konservative Wirtschaftlichkeitsanalyse (Marktpreis-Obergrenzen 2025/2026) für folgendes Gebäude. Wenn Angaben fehlen, treffe begründete Annahmen und liste fehlende Felder in "missingData" auf.

ADRESSE: ${data.street} ${data.houseNumber}, ${data.zip} ${data.city}
HAUS: ${data.houseType}, ${data.units || "?"} WE, Baujahr ${data.yearBuilt}, Bauweise ${data.construction}
DACH: ${data.roofType}, Dämmung Dachstuhl: ${data.roofInsulated}
FENSTER: ${data.windowMaterial} / ${data.windowGlazing}
TÜREN: Eingangstür ${data.entranceDoorMaterial} (${data.entranceDoorCondition}), Innentüren ${data.interiorDoorsMaterial} (${data.interiorDoorsCondition})
SANIERUNG ERFOLGT: ${data.renovationsDone} – ${data.renovationsDetails}
HEIZUNG: ${data.heatingType}, Verbrauch ${data.heatingConsumption}
STROM: ${data.electricityConsumption}, Anbieter ${data.electricityProvider}
ENERGIEAUSWEIS: ${data.energyCertificate} – Klasse ${data.energyClass}
STEUERN: Klasse ${data.taxClass}, Steuersatz ${data.taxRate}%, zvE ${data.taxableIncome} €
VORHABEN: ${data.projectGoal} – ${data.projectDescription}
ZUSATZ: ${data.freeText}

PFLICHTFELDER (immer ausfüllen):
- projectSummary (string, 2-4 Sätze Zusammenfassung)
- detectedMeasures (Array, Maßnahmen mit massnahme/begruendung/prioritaet)
- risks (Array, risiko/schwere)
- recommendations (Array, empfehlung/prioritaet)
- trustLevel ("A"|"B"|"C"|"D"|"E", basierend auf Datenqualität)
- missingData (Array von fehlenden Eingabefeldern, z.B. ["heatingConsumption","yearBuilt"])

OPTIONALE FELDER (so weit möglich ausfüllen):
standort, photovoltaik, waermepumpe, sanierung, foerderung, steuer, istSituation, zukunftSituation, investitionGesamt, empfehlung, hinweise.

Alle Geldbeträge als reine Zahl in Euro (keine Tausender-Trennzeichen, keine Strings). Antworte AUSSCHLIESSLICH als JSON gemäß dem vorgegebenen Schema.`;

    // 1. Versuch: strukturierte Ausgabe via Output.object
    try {
      const { experimental_output } = await generateText({
        model,
        prompt,
        maxOutputTokens: 4000,
        experimental_output: Output.object({ schema: AnalysisSchema }),
      });
      return { ok: true as const, analysis: experimental_output };
    } catch (e1) {
      console.warn("[runProjectAnalysis] strukturierte Ausgabe fehlgeschlagen:", e1 instanceof Error ? e1.message : e1);

      // 2. Versuch: rohe Ausgabe parsen + repair
      try {
        const { text } = await generateText({
          model,
          prompt: prompt + "\n\nGib ausschließlich ein einzelnes JSON-Objekt zurück, kein Markdown.",
          maxOutputTokens: 4000,
        });
        const raw = extractJsonFromResponse(text);
        if (raw) {
          const parsed = AnalysisSchema.safeParse(raw);
          if (parsed.success) {
            return { ok: true as const, analysis: parsed.data };
          }
          console.warn("[runProjectAnalysis] Schema-Validierung fehlgeschlagen:", parsed.error.issues.slice(0, 5));
          // Versuche partial-merge
          const fallback = buildFallback(raw as Partial<AnalysisResult>, "Antwort des Modells konnte nur teilweise validiert werden.");
          return { ok: true as const, analysis: fallback };
        }
        console.error("[runProjectAnalysis] kein JSON in Antwort gefunden. Roh-Text:", text.slice(0, 500));
      } catch (e2) {
        console.error("[runProjectAnalysis] Fallback-Generierung fehlgeschlagen:", e2 instanceof Error ? e2.stack : e2);
      }

      // 3. Letzter Fallback: leeres valides Objekt
      const fb = buildFallback(null, "KI-Antwort konnte nicht strukturiert verarbeitet werden.");
      return {
        ok: false as const,
        error: "Die Analyse konnte nicht vollständig strukturiert werden. Bitte prüfen Sie die Eingaben oder starten Sie die Analyse erneut.",
        analysis: fb,
      };
    }
  });

// ---------------------------------------------------------------------------
// Test-Helper: liefert garantiert valides Analyseobjekt bei minimalem Input.
// Wird in Tests verwendet (siehe analysis-fallback.test).
// ---------------------------------------------------------------------------
export function __test_buildFallbackAnalysis(reason = "test"): AnalysisResult {
  return buildFallback(null, reason);
}

export function __test_repairAnalysis(raw: unknown): AnalysisResult {
  const parsed = AnalysisSchema.safeParse(raw);
  if (parsed.success) return parsed.data;
  return buildFallback(raw as Partial<AnalysisResult>, "partial");
}
